import { useEffect, useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import { PIN_LENGTH } from 'utils/constants';
import { getResponsiveSize } from 'utils/index';

import NewGradientButton from 'components/Button/NewGradienteButton';
import { PinModalProps } from 'components/Pin/PinModal';
import { ProfilePinField } from 'components/Profile/Form/PinField';

export const WebPinModal = ({
  variant = 'verify',
  isOpen,
  error,
  onConfirm,
  onCancel,
}: PinModalProps) => {
  const [pin, setPin] = useState('');

  const isVerify = variant === 'verify';
  const title = isVerify ? 'Insira seu PIN para continuar' : 'Criar PIN da conta';
  const textBolt = isVerify
    ? `Insira seu PIN de ${PIN_LENGTH} dígitos para continuar.`
    : 'Use seu PIN para manter sua conta segura ao acessar ou alterar seus dados.';
  const text = 'Se precisar, você pode defini-lo novamente na edição de perfil.';

  const handlePinConfirm = () => {
    if (pin.length === PIN_LENGTH) onConfirm(pin);
  };

  useEffect(() => {
    if (!isOpen) setPin('');
  }, [isOpen]);

  useEffect(() => {
    if (pin.length === PIN_LENGTH && variant === 'verify') {
      onConfirm(pin);
    }
  }, [pin, variant, onConfirm]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setPin('');
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [error]);

  if (!isOpen) return null;

  return (
    <Modal visible={isOpen} transparent animationType="fade">
      <View style={styles.overlay}>
        <View style={styles.container}>
          <Text style={styles.title}>{title}</Text>
          <Text style={[styles.text, { fontWeight: '600' }]}>{textBolt}</Text>
          <Text style={[styles.text, { marginTop: 12, marginBottom: 32 }]}>{text}</Text>

          <View style={styles.elementContainer}>
            <ProfilePinField value={pin} onChange={setPin} error={error} />
          </View>

          <View style={styles.elementContainer}>
            {isVerify ? (
              <Pressable accessibilityRole="button" style={styles.cancelBtn} onPress={onCancel}>
                <Text style={styles.cancelBtnText}>Cancelar</Text>
              </Pressable>
            ) : (
              <NewGradientButton style={styles.saveBtn} onPress={handlePinConfirm}>
                <Text style={styles.saveBtnText}>Salvar PIN</Text>
              </NewGradientButton>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0,0,0,0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: Number(getResponsiveSize({ base: 16, md: 24 })),
  },
  container: {
    width: '90%',
    maxWidth: 829,
    paddingHorizontal: Number(getResponsiveSize({ base: 16, md: 48 })),
    paddingVertical: Number(getResponsiveSize({ base: 24, md: 32 })),
    backgroundColor: '#050923',
    borderWidth: 2,
    borderRadius: 32,
    borderColor: '#444444',
  },
  title: {
    fontSize: Number(getResponsiveSize({ base: 28, md: 36 })),
    fontWeight: '700',
    color: '#7A8CFF',
    textAlign: 'center',
    marginBottom: 32,
  },
  text: {
    fontSize: Number(getResponsiveSize({ base: 16, md: 20 })),
    fontWeight: '400',
    color: '#FFF',
    textAlign: 'center',
  },
  elementContainer: {
    alignSelf: 'center',
    marginBottom: Number(getResponsiveSize({ base: 24, md: 32 })),
  },
  saveBtn: {
    height: 54,
  },
  saveBtnText: {
    fontSize: 20,
    fontWeight: '600',
  },
  cancelBtn: {
    width: '100%',
    height: Number(getResponsiveSize({ base: 34, md: 54 })),
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    marginHorizontal: Number(getResponsiveSize({ base: 24, md: 54 })),
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    outlineStyle: 'none',
  },
  cancelBtnText: {
    fontSize: Number(getResponsiveSize({ base: 16, md: 20 })),
    fontWeight: '600',
    color: '#FFF',
  },
});
